import axios from 'axios';
import server from "./server";

// Crear instancia optimizada de axios
const clienteAxios = axios.create({
    baseURL: server.baseURL,
    timeout: 10000, // 10 segundos timeout
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
    },
    // Optimizaciones de rendimiento
    maxContentLength: 50000000, // 50MB
    maxBodyLength: 50000000,
    // Comprimir respuestas
    decompress: true,
});

// Cache para requests repetidos
const requestCache = new Map();
const cacheTimeout = 30000; // 30 segundos

// Interceptor de request - Agregar cache y optimizaciones
clienteAxios.interceptors.request.use(
    (config) => {
        const startTime = Date.now();
        config.metadata = { startTime };

        // Solo cachear GET requests
        if (config.method === 'get') {
            const cacheKey = `${config.url}?${JSON.stringify(config.params || {})}`;
            const cached = requestCache.get(cacheKey);
            
            if (cached && Date.now() - cached.timestamp < cacheTimeout) {
                // Retornar respuesta cacheada
                return Promise.reject({
                    __CACHED_RESPONSE__: cached.data,
                    config
                });
            }
        }

        // Agregar headers de optimización
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        
        // Comprimir request si es necesario
        if (config.data && typeof config.data === 'object') {
            config.headers['Accept-Encoding'] = 'gzip, deflate, br';
        }

        if (typeof process !== 'undefined' && process.env.NODE_ENV === 'development') {
            console.log(`📡 API Request: ${config.method?.toUpperCase()} ${config.url}`);
        }

        return config;
    },
    (error) => {
        console.error('❌ Request Error:', error);
        return Promise.reject(error);
    }
);

// Interceptor de response - Manejar cache y métricas
clienteAxios.interceptors.response.use(
    (response) => {
        const { config } = response;
        const endTime = Date.now();
        const duration = endTime - (config.metadata?.startTime || endTime);

        // Guardar en cache solo responses exitosas de GET
        if (config.method === 'get' && response.status === 200) {
            const cacheKey = `${config.url}?${JSON.stringify(config.params || {})}`;
            requestCache.set(cacheKey, {
                data: response,
                timestamp: Date.now()
            });

            // Limpiar cache viejo
            if (requestCache.size > 100) {
                const oldestKey = requestCache.keys().next().value;
                requestCache.delete(oldestKey);
            }
        }

        if (typeof process !== 'undefined' && process.env.NODE_ENV === 'development') {
            console.log(`✅ API Response: ${config.method?.toUpperCase()} ${config.url} (${duration}ms)`);
        }

        return response;
    },
    (error) => {
        // Manejar respuestas cacheadas
        if (error.__CACHED_RESPONSE__) {
            if (typeof process !== 'undefined' && process.env.NODE_ENV === 'development') {
                console.log(`💾 Cache HIT: ${error.config.url}`);
            }
            return Promise.resolve(error.__CACHED_RESPONSE__);
        }

        const { config } = error;
        const endTime = Date.now();
        const duration = endTime - (config?.metadata?.startTime || endTime);

        console.error(`❌ API Error: ${config?.method?.toUpperCase()} ${config?.url} (${duration}ms)`, error.response?.status);

        // Reintentar automáticamente en ciertos casos
        if (error.response?.status >= 500 && !config._retry) {
            config._retry = true;
            console.log('🔄 Retrying request...');
            return clienteAxios(config);
        }

        return Promise.reject(error);
    }
);

// Función para limpiar cache manualmente
export const clearAxiosCache = () => {
    requestCache.clear();
    console.log('🧹 Axios cache cleared');
};

// Función para obtener estadísticas del cache
export const getAxiosCacheStats = () => {
    return {
        size: requestCache.size,
        keys: Array.from(requestCache.keys()),
        totalSize: JSON.stringify([...requestCache.values()]).length
    };
};

export default clienteAxios;